import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Package, Clock, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { WidgetPanel, StatCard } from './DashboardShared';
import { stockRows, catalogProducts, stockHistoryOf } from '../../../data/store';

const getProductName = (productId) => {
  const prod = catalogProducts.find(p => p.id === productId);
  return prod ? prod.name : `Unknown Product (${productId})`;
};

export const InventoryModule = ({ org }) => {
  const metrics = useMemo(() => {
    const inv = stockRows.filter(r => r.orgId === org.id && r.note !== 'T0 Stock');
    
    let activeStock = 0;
    let pendingCount = 0;
    let stockValue = 0;
    const noStock = [];
    const lowStock = [];
    // Sorted on updatedAt, which is the column a stock row actually has. It
    // sorted on createdAt, and StockRow carries no such field — so every row
    // compared as new Date(0), the sort did nothing at all, and a card headed
    // "recently added" was showing five arbitrary rows in whatever order they
    // arrived.
    const byRecency = [...inv].sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
    );
    const biggestAdd = byRecency.slice(0, 5).reduce((m, r) => Math.max(m, Number(r.qty) || 0), 0);
    const recent = byRecency.slice(0, 5).map((r) => ({
      ...r,
      share: biggestAdd > 0 ? (Number(r.qty) || 0) / biggestAdd : 0,
    }));

    inv.forEach(r => {
      const qty = Number(r.qty) || 0;
      if (r.status === 'pending') {
        pendingCount++;
      } else if (r.status === 'active') {
        activeStock += qty;
        stockValue += qty * (Number(r.costPrice) || 0);
        
        if (qty === 0) {
          noStock.push(r);
        } else {
          const threshold = Number(r.minStockThreshold) || 0;
          if (threshold > 0 && qty <= threshold) lowStock.push(r);
        }
      }
    });

    // Why stock moved, and how much of it. The card that used to sit here said
    // "Movement Chart Available Soon" and drew nothing — a panel of dashboard
    // holding a promise. Every movement is already recorded with a reason, so
    // the answer was in the data the whole time.
    const byReason = new Map();
    stockHistoryOf(org.id).forEach((h) => {
      const reason = (h.reason || 'Unspecified').trim() || 'Unspecified';
      const at = byReason.get(reason) || { reason, qty: 0, moves: 0 };
      at.qty += Number(h.qty) || 0;
      at.moves += 1;
      byReason.set(reason, at);
    });
    const orderedMoves = [...byReason.values()].sort((a, b) => b.qty - a.qty);
    const biggestMove = orderedMoves.length ? orderedMoves[0].qty : 0;
    const movement = orderedMoves.slice(0, 5).map((m) => ({
      ...m,
      share: biggestMove > 0 ? m.qty / biggestMove : 0,
    }));

    return { activeStock, pendingCount, stockValue, noStock, lowStock, recent, movement };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <Package size={16} className="text-[var(--theme-primary)]" />
        Inventory
      </div>
      
      <div className="dash-enter grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Package} label="Active Stock Units" value={metrics.activeStock} iconColor="#8b5cf6" />
        <StatCard icon={Clock} label="Pending Inventory" value={metrics.pendingCount} suffix="rows" iconColor="#f59e0b" />
        <StatCard icon={AlertTriangle} label="Stock Value (At Cost)" value={`₹${metrics.stockValue.toFixed(2)}`} iconColor="#059669" />
      </div>

      {(metrics.noStock.length > 0 || metrics.lowStock.length > 0) && (
        <div className="dash-enter grid grid-cols-1 lg:grid-cols-2 gap-4">
          {metrics.noStock.length > 0 && (
            <WidgetPanel title="⚠️ No-Stock Items" count={metrics.noStock.length} className="!border-rose-200 bg-rose-50/30">
              <ul className="divide-y divide-rose-100 mt-2">
                {metrics.noStock.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="py-2.5 flex items-center justify-between text-xs">
                    <span className="font-bold text-rose-900 truncate pr-4">{getProductName(item.productId)}</span>
                    <span className="font-bold text-rose-600 bg-rose-100 px-2 py-0.5 rounded shrink-0">0 left</span>
                  </li>
                ))}
              </ul>
            </WidgetPanel>
          )}
          {metrics.lowStock.length > 0 && (
            <WidgetPanel title="⚠️ Low-Stock Items" count={metrics.lowStock.length} className="!border-orange-200 bg-orange-50/30">
              <ul className="divide-y divide-orange-100 mt-2">
                {metrics.lowStock.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="py-2.5 flex items-center justify-between text-xs">
                    <span className="font-bold text-orange-900 truncate pr-4">{getProductName(item.productId)}</span>
                    <span className="font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded shrink-0">{item.qty} left</span>
                  </li>
                ))}
              </ul>
            </WidgetPanel>
          )}
        </div>
      )}

      <div className="dash-enter grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Both cards share the row the Top Selling card uses: a marker, the
            name with a bar beneath it, and the figures on the right. A reader
            who has learned one row on this dashboard has learned them all. */}
        <WidgetPanel title="Recently Added Stock">
          <ul className="flex flex-col divide-y divide-slate-50">
            {metrics.recent.length === 0 && (
              <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No inventory yet</li>
            )}
            {metrics.recent.map((item) => {
              const active = item.status === 'active';
              const colour = active ? '#10b981' : '#f59e0b';
              return (
                <li key={item.id} className="flex items-center gap-2.5 py-2 min-w-0">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: `${colour}1a` }}>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colour }} />
                  </span>

                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-[12px] font-bold text-[#0d0d0d]" title={getProductName(item.productId)}>
                      {getProductName(item.productId)}
                    </span>
                    <span className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                      <span className="block h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max(item.share * 100, 3)}%`, backgroundColor: colour }} />
                    </span>
                  </span>

                  <span className="flex shrink-0 flex-col items-end">
                    <span className="text-[12px] font-bold text-[#0d0d0d] tabular-nums">+{item.qty}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colour }}>
                      {item.status}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Stock Movement">
          <ul className="flex flex-col divide-y divide-slate-50">
            {metrics.movement.length === 0 && (
              <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Nothing has moved yet</li>
            )}
            {metrics.movement.map((m) => (
              <li key={m.reason} className="flex items-center gap-2.5 py-2 min-w-0">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-violet-50">
                  <ArrowRightLeft size={11} className="text-violet-500" />
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-[12px] font-bold text-[#0d0d0d]" title={m.reason}>{m.reason}</span>
                  <span className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full bg-violet-500 transition-all duration-700 ease-out"
                      style={{ width: `${Math.max(m.share * 100, 3)}%` }} />
                  </span>
                </span>

                <span className="flex shrink-0 flex-col items-end">
                  <span className="text-[12px] font-bold text-[#0d0d0d] tabular-nums">{m.qty}</span>
                  <span className="text-[10px] font-semibold text-slate-400 tabular-nums">
                    {m.moves} move{m.moves === 1 ? '' : 's'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </WidgetPanel>
      </div>
    </div>
  );
};

InventoryModule.propTypes = {
  org: PropTypes.object.isRequired,
};
